import {
  type FilterKey,
  type FilterProvider,
  FilterProviderType,
  type FilterSuggestion,
  ProviderSupportStatus,
  type SuppliedFilterProviderOptions,
} from '@github-ui/filter'
import {BooleanFilterProvider, SelectFilterProvider} from '@github-ui/filter/providers'
import {singleFilterProviderOptions} from '@github-ui/filter/utils'
import {
  AppsIcon,
  ArchiveIcon,
  CalendarIcon,
  CodeIcon,
  EyeIcon,
  FileIcon,
  GearIcon,
  HeartIcon,
  IssueOpenedIcon,
  LawIcon,
  MirrorIcon,
  OrganizationIcon,
  PackageDependentsIcon,
  PersonIcon,
  RepoForkedIcon,
  RepoTemplateIcon,
  SortAscIcon,
  StarIcon,
  TelescopeIcon,
  TypographyIcon,
} from '@primer/octicons-react'

export interface ReposFilterAdditionalOptions {
  withOwnerTerms?: boolean
  withFinderTerms?: boolean
  withRelevanceSort?: boolean
  includeDeploymentProperties?: boolean
}

const comma: SuppliedFilterProviderOptions = {filterTypes: {multiKey: false, multiValue: true, valueless: false}}
const multiKey: SuppliedFilterProviderOptions = {filterTypes: {multiKey: true, multiValue: false, valueless: false}}
const multiComma: SuppliedFilterProviderOptions = {filterTypes: {multiKey: true, multiValue: true, valueless: false}}

export function getAllStaticProviders(
  orgs: string[] = [],
  options: ReposFilterAdditionalOptions = {},
): FilterProvider[] {
  const {withRelevanceSort, withFinderTerms, withOwnerTerms, includeDeploymentProperties} = options

  const providers: FilterProvider[] = [
    new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.has, REPOS_STATIC_FILTER_VALUES.has, singleFilterProviderOptions),
    new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.in, REPOS_STATIC_FILTER_VALUES.in, comma),
    new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.is, REPOS_STATIC_FILTER_VALUES.is, {
      ...comma,
      support: {
        status: ProviderSupportStatus.Deprecated,
        message: 'Filter "is:" is deprecated, use "visibility:" or "sponsorable:" instead',
      },
    }),
    new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.license, REPOS_STATIC_FILTER_VALUES.license, comma),
    new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.topic, REPOS_STATIC_FILTER_VALUES.topic, multiComma),
    new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.visibility, REPOS_STATIC_FILTER_VALUES.visibility, comma),

    new BooleanFilterProvider(REPOS_STATIC_FILTER_KEYS.fork),
    new BooleanFilterProvider(REPOS_STATIC_FILTER_KEYS.archived),
    new BooleanFilterProvider(REPOS_STATIC_FILTER_KEYS.mirror),
    new BooleanFilterProvider(REPOS_STATIC_FILTER_KEYS.sponsorable),
    new BooleanFilterProvider(REPOS_STATIC_FILTER_KEYS.template),
  ]

  const sortingValues = withRelevanceSort
    ? REPOS_STATIC_FILTER_VALUES.sort
    : REPOS_STATIC_FILTER_VALUES.sort.filter(sort => sort.value !== 'relevance')

  providers.push(new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.sort, sortingValues, multiKey))

  if (includeDeploymentProperties) {
    providers.push(
      new BooleanFilterProvider(REPOS_STATIC_FILTER_KEYS.deployable),
      new BooleanFilterProvider(REPOS_STATIC_FILTER_KEYS.deployed),
    )
  }

  if (withFinderTerms) {
    providers.push(
      new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.adminableBy, REPOS_STATIC_FILTER_VALUES.adminableBy),
      new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.contributedBy, REPOS_STATIC_FILTER_VALUES.contributedBy),
    )
  }

  if (withOwnerTerms) {
    providers.push(
      new ValuesFilterProvider(REPOS_STATIC_FILTER_KEYS.org, getOrgValues(orgs), multiComma, FilterProviderType.Text),
      new ValuesFilterProvider(
        REPOS_STATIC_FILTER_KEYS.owner,
        REPOS_STATIC_FILTER_VALUES.user,
        multiComma,
        FilterProviderType.Text,
      ),
      new ValuesFilterProvider(
        REPOS_STATIC_FILTER_KEYS.user,
        REPOS_STATIC_FILTER_VALUES.user,
        multiComma,
        FilterProviderType.Text,
      ),
    )
  }

  return providers
}

export function getRepoFilterProviders(
  keys: ReposStaticProviderKey[],
  options?: ReposFilterAdditionalOptions,
): FilterProvider[] {
  return getAllStaticProviders(undefined, options).filter(({key}) => keys.includes(key as ReposStaticProviderKey))
}

const NOT_SHOWN = 10

export type ReposStaticProviderKey = keyof typeof REPOS_STATIC_FILTER_KEYS
const REPOS_STATIC_FILTER_KEYS = {
  adminableBy: {displayName: 'Adminable', key: 'adminable-by', description: '', priority: NOT_SHOWN, icon: GearIcon},
  archived: {displayName: 'Archived', key: 'archived', description: '', priority: NOT_SHOWN, icon: ArchiveIcon},
  contributedBy: {
    displayName: 'Contributions',
    key: 'contributed-by',
    description: '',
    priority: NOT_SHOWN,
    icon: PersonIcon,
  },
  fork: {displayName: 'Fork', key: 'fork', description: '', priority: 3, icon: RepoForkedIcon},
  has: {displayName: 'Has', key: 'has', description: '', priority: NOT_SHOWN, icon: FileIcon},
  in: {displayName: 'In', key: 'in', description: '', priority: NOT_SHOWN, icon: AppsIcon},
  is: {displayName: 'Is', key: 'is', description: '', priority: NOT_SHOWN, icon: EyeIcon},
  license: {displayName: 'License', key: 'license', description: '', priority: 4, icon: LawIcon},
  mirror: {displayName: 'Mirror', key: 'mirror', description: '', priority: 3, icon: MirrorIcon},
  org: {displayName: 'Organization', key: 'org', description: '', priority: NOT_SHOWN, icon: OrganizationIcon},
  owner: {displayName: 'Owner', key: 'owner', description: '', priority: NOT_SHOWN, icon: PersonIcon},
  sort: {displayName: 'Sort', key: 'sort', description: 'Order of results', priority: NOT_SHOWN, icon: SortAscIcon},
  sponsorable: {
    displayName: 'Sponsorable',
    key: 'sponsorable',
    description: '',
    priority: NOT_SHOWN,
    icon: HeartIcon,
  },
  template: {displayName: 'Template', key: 'template', description: '', priority: NOT_SHOWN, icon: RepoTemplateIcon},
  topic: {displayName: 'Topic', key: 'topic', priority: NOT_SHOWN, icon: TelescopeIcon},
  user: {displayName: 'User', key: 'user', description: '', priority: NOT_SHOWN, icon: PersonIcon},
  visibility: {displayName: 'Visibility', key: 'visibility', description: '', priority: 3, icon: EyeIcon},
  deployable: {displayName: 'Deployable', key: 'deployable', description: '', priority: 3, icon: PackageDependentsIcon},
  deployed: {displayName: 'Deployed', key: 'deployed', description: '', priority: 3, icon: PackageDependentsIcon},
}

const VISIBILITY_FILTER_VALUES = [
  {value: 'public', displayName: 'Public', priority: 1},
  {value: 'private', displayName: 'Private', priority: 2},
  {value: 'internal', displayName: 'Internal', priority: 3},
]

const REPOS_STATIC_FILTER_VALUES = {
  adminableBy: [{value: '@me', displayName: 'Me', priority: 1}],
  contributedBy: [{value: '@me', displayName: 'Me', priority: 1}],
  has: [{value: 'funding-file', displayName: 'Funding file', priority: 1}],
  is: [...VISIBILITY_FILTER_VALUES, {value: 'sponsorable', displayName: 'Sponsorable', priority: 4}],
  in: [
    {value: 'name', displayName: 'Name', priority: 1},
    {value: 'description', displayName: 'Description', priority: 2},
    {value: 'readme', displayName: 'README', priority: 3},
    {value: 'topics', displayName: 'Topics', priority: 4},
  ],
  license: [
    {value: '0bsd', displayName: 'BSD Zero Clause License', priority: 1},
    {value: 'mit', displayName: 'MIT License', priority: 2},
    {value: 'apache-2.0', displayName: 'Apache License 2.0', priority: 3},
    {value: 'cc', displayName: 'Creative Commons', priority: 4},
    {value: 'gpl', displayName: 'GNU General Public License', priority: 5},
    {value: 'lgpl', displayName: 'GNU Lesser General Public License', priority: 6},
  ],
  sort: [
    {value: 'updated', displayName: 'Recently pushed', icon: CalendarIcon, priority: 1},
    {value: 'name-asc', displayName: 'Name', icon: TypographyIcon, priority: 2},
    {value: 'language-asc', displayName: 'Language', icon: CodeIcon, priority: 3},
    {value: 'license-asc', displayName: 'License', icon: LawIcon, priority: 3},
    {value: 'topics', displayName: 'Topics', icon: TelescopeIcon, priority: 3},
    {value: 'size', displayName: 'Size', icon: FileIcon, priority: 3},
    {value: 'stars', displayName: 'Total stars', icon: StarIcon, priority: 3},
    {value: 'forks', displayName: 'Total forks', icon: RepoForkedIcon, priority: 3},
    {value: 'relevance', displayName: 'Relevance', icon: PersonIcon, priority: 3},
    {value: 'updated-asc', displayName: 'Recently pushed (descending)', icon: CalendarIcon, priority: NOT_SHOWN},
    {value: 'name', displayName: 'Name (descending)', icon: TypographyIcon, priority: NOT_SHOWN},
    {value: 'language', displayName: 'Language (descending)', icon: CodeIcon, priority: NOT_SHOWN},
    {value: 'license', displayName: 'License (descending)', icon: LawIcon, priority: NOT_SHOWN},
    {value: 'topics-asc', displayName: 'Topics (ascending)', icon: TelescopeIcon, priority: NOT_SHOWN},
    {value: 'size-asc', displayName: 'Size (ascending)', icon: FileIcon, priority: NOT_SHOWN},
    {value: 'stars-asc', displayName: 'Total stars (ascending)', icon: StarIcon, priority: NOT_SHOWN},
    {value: 'forks-asc', displayName: 'Total forks (ascending)', icon: RepoForkedIcon, priority: NOT_SHOWN},
    {
      value: 'help-wanted-issues-asc',
      displayName: 'Help-wanted issues (ascending)',
      icon: IssueOpenedIcon,
      priority: 5,
    },
    {value: 'help-wanted-issues', displayName: 'Help-wanted issues (descending)', icon: IssueOpenedIcon, priority: 5},
  ],
  topic: [],
  user: [{value: '@me', displayName: 'Me', priority: 1}],
  visibility: VISIBILITY_FILTER_VALUES,
}

function getOrgValues(orgs: string[]): FilterSuggestion[] {
  return orgs.map(org => ({
    value: org,
    displayName: org,
    priority: 1,
    icon: OrganizationIcon,
  }))
}

class ValuesFilterProvider extends SelectFilterProvider {
  constructor(
    filter: FilterKey,
    values: FilterSuggestion[],
    options?: SuppliedFilterProviderOptions,
    type?: FilterProviderType,
  ) {
    super(filter, values, {...options, strictMatching: type !== FilterProviderType.Text && values.length > 0})
  }
}
